import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, User, Globe, Image, Save, ExternalLink, Link, Calendar, Copy, CheckCircle2, XCircle, Settings } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { getAvatarInitials, getAvatarSource } from "@/lib/avatar";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useLocation } from "wouter";
import type { Talent, UserProfile } from "@shared/schema";
import { PressKitEditor } from "@/components/press-kit-editor";

interface PressKitSettings { pressKitApprovalMode: "off" | "talent_to_admin" | "rep_to_talent"; pendingCount: number }

export default function TalentProfilePage() {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const [copied, setCopied] = useState(false);

  const { data: profile, isLoading: profileLoading } = useQuery<UserProfile>({
    queryKey: ["/api/profile"],
  });

  const { data: talent = [], isLoading: talentLoading } = useQuery<Talent[]>({
    queryKey: ["/api/talent"],
  });

  const { data: calendarStatus } = useQuery<{ connected: boolean; email: string | null }>({
    queryKey: ["/api/calendar/status"],
  });

  const myTalent = talent.find(e => e.userId === profile?.userId);

  // Talent users get 403 from this admin endpoint; the PATCH handler still
  // routes their edits through the queue when the workspace mode requires it,
  // so we can fall back to a single safe assumption (the talent's own button
  // shows "Submit for approval" only when the server confirmed that mode).
  const { data: pressKitSettings } = useQuery<PressKitSettings>({
    queryKey: ["/api/admin/press-kit-settings"],
    retry: false,
  });
  const awaitingApproval = pressKitSettings?.pressKitApprovalMode === "talent_to_admin";
  
  // Generate the booking URL
  const bookingUrl = myTalent?.bookingSlug 
    ? `${window.location.origin}/book/${myTalent.bookingSlug}`
    : null;

  const handleCopyLink = () => {
    if (bookingUrl) {
      navigator.clipboard.writeText(bookingUrl);
      setCopied(true);
      toast({ title: "Copied!", description: "Booking link copied to clipboard." });
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const [formData, setFormData] = useState({
    name: "",
    photoUrl: "",
    bio: "",
    eventDescription: "",
    websiteUrl: "",
    socialMediaUrls: "",
    googleDriveUrl: "",
  });

  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (myTalent) {
      setFormData({
        name: myTalent.name || "",
        photoUrl: myTalent.photoUrl || "",
        bio: myTalent.bio || "",
        eventDescription: myTalent.eventDescription || "",
        websiteUrl: myTalent.websiteUrl || "",
        socialMediaUrls: myTalent.socialMediaUrls?.[0] || "",
        googleDriveUrl: myTalent.googleDriveUrl || "",
      });
    }
  }, [myTalent]);

  const updateMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (!myTalent) throw new Error("No talent profile found");
      const response = await apiRequest("PATCH", `/api/talent/${myTalent.id}`, data);
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/talent"] });
      toast({ title: "Profile Updated", description: "Your talent profile has been saved." });
      setHasChanges(false);
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message || "Failed to update profile.", variant: "destructive" });
    },
  });

  const handleChange = (field: keyof typeof formData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (profileLoading || talentLoading) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!myTalent) {
    return (
      <div className="flex-1 p-6">
        <div className="max-w-2xl mx-auto text-center py-12">
          <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-muted-foreground" />
          </div>
          <h1 className="text-2xl font-bold mb-2">No Talent Profile</h1>
          <p className="text-muted-foreground">
            You don't have an talent profile linked to your account yet. 
            Please contact an administrator to set up your talent profile.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-auto p-6">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Avatar className="w-16 h-16">
              <AvatarImage
                src={getAvatarSource({ photoUrl: formData.photoUrl })}
                alt={formData.name}
                className="object-cover"
              />
              <AvatarFallback className="bg-muted">
                {getAvatarInitials(formData.name)}
              </AvatarFallback>
            </Avatar>
            <div>
              <h1 className="text-2xl font-bold" data-testid="text-talent-profile-title">
                {formData.name || "My Profile"}
              </h1>
              <p className="text-muted-foreground">Manage your talent profile and information</p>
            </div>
          </div>
          <Badge variant={myTalent.isActive ? "default" : "secondary"}>
            {myTalent.isActive ? "Active" : "Inactive"}
          </Badge>
        </div>

        {/* Booking Link & Calendar Status */}
        <Card className="border-primary/20 bg-primary/5">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2">
              <Link className="w-5 h-5" />
              Your Booking Link
            </CardTitle>
            <CardDescription>
              Share this link with clients to let them request bookings directly
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {bookingUrl ? (
              <div className="flex items-center gap-2">
                <Input
                  value={bookingUrl}
                  readOnly
                  className="font-mono text-sm"
                  data-testid="input-booking-link"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={handleCopyLink}
                  data-testid="button-copy-booking-link"
                >
                  {copied ? <CheckCircle2 className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                </Button>
                <Button
                  variant="outline"
                  size="icon"
                  asChild
                >
                  <a href={bookingUrl} target="_blank" rel="noopener noreferrer" data-testid="link-preview-booking">
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </Button>
              </div>
            ) : (
              <Alert>
                <AlertDescription>
                  No booking link available. Contact an administrator to set up your booking page.
                </AlertDescription>
              </Alert>
            )}

            <Separator />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${calendarStatus?.connected ? 'bg-green-100 dark:bg-green-900/30' : 'bg-muted'}`}>
                  <Calendar className={`w-5 h-5 ${calendarStatus?.connected ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="font-medium">Calendar Sync</span>
                    {calendarStatus?.connected ? (
                      <CheckCircle2 className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {calendarStatus?.connected 
                      ? `Connected: ${calendarStatus.email}`
                      : 'Connect your Google Calendar to show availability on your booking page'}
                  </p>
                </div>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate("/settings")}
                data-testid="button-calendar-settings"
              >
                <Settings className="w-4 h-4 mr-2" />
                {calendarStatus?.connected ? 'Manage' : 'Connect'}
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="w-5 h-5" />
              Basic Information
            </CardTitle>
            <CardDescription>
              Your public profile information displayed to clients and on bookings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="name">Display Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => handleChange("name", e.target.value)}
                  placeholder="Your stage name"
                  data-testid="input-talent-profile-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="photoUrl" className="flex items-center gap-2">
                  <Image className="w-3 h-3" />
                  Photo URL
                </Label>
                <Input
                  id="photoUrl"
                  value={formData.photoUrl}
                  onChange={(e) => handleChange("photoUrl", e.target.value)}
                  placeholder="https://example.com/photo.jpg"
                  data-testid="input-talent-profile-photo"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={formData.bio}
                onChange={(e) => handleChange("bio", e.target.value)}
                placeholder="Tell clients about yourself..."
                rows={4}
                data-testid="input-talent-profile-bio"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="eventDescription">Event Description</Label>
              <Textarea
                id="eventDescription"
                value={formData.eventDescription}
                onChange={(e) => handleChange("eventDescription", e.target.value)}
                placeholder="Describe your show and what makes it special..."
                rows={4}
                data-testid="input-talent-profile-show"
              />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Online Presence
            </CardTitle>
            <CardDescription>
              Links to your website and social media
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="websiteUrl">Website</Label>
              <div className="flex gap-2">
                <Input
                  id="websiteUrl"
                  value={formData.websiteUrl}
                  onChange={(e) => handleChange("websiteUrl", e.target.value)}
                  placeholder="https://yourwebsite.com"
                  data-testid="input-talent-profile-website"
                />
                {formData.websiteUrl && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={formData.websiteUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="socialMediaUrls">Social Media</Label>
              <div className="flex gap-2">
                <Input
                  id="socialMediaUrls"
                  value={formData.socialMediaUrls}
                  onChange={(e) => handleChange("socialMediaUrls", e.target.value)}
                  placeholder="https://instagram.com/yourusername"
                  data-testid="input-talent-profile-social"
                />
                {formData.socialMediaUrls && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={formData.socialMediaUrls} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="googleDriveUrl">EPK Link</Label>
              <div className="flex gap-2">
                <Input
                  id="googleDriveUrl"
                  value={formData.googleDriveUrl}
                  onChange={(e) => handleChange("googleDriveUrl", e.target.value)}
                  placeholder="https://drive.google.com/drive/folders/..."
                  data-testid="input-talent-profile-drive"
                />
                {formData.googleDriveUrl && (
                  <Button variant="outline" size="icon" asChild>
                    <a href={formData.googleDriveUrl} target="_blank" rel="noopener noreferrer">
                      <ExternalLink className="w-4 h-4" />
                    </a>
                  </Button>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Public/shareable link to your EPK, promotional materials, and media assets. No Google Drive connection required.
              </p>
            </div>
          </CardContent>
        </Card>

        {myTalent && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5" />
                Public Press Kit
              </CardTitle>
              <CardDescription>
                Hero, bio, media, credits, docs, FAQ, contact card. Published page lives at <code className="text-xs">/artist/{myTalent.bookingSlug}</code>.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PressKitEditor talent={myTalent} awaitingApproval={awaitingApproval} />
            </CardContent>
          </Card>
        )}

        <div className="flex justify-end">
          <Button 
            onClick={handleSave} 
            disabled={updateMutation.isPending || !hasChanges}
            data-testid="button-save-talent-profile"
          >
            {updateMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                Save Changes
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
